import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';

import * as io from 'socket.io-client';

import { SocketIoConfig } from './config/socket-io.config';

export class WrappedSocket {
  subscribersCounter: Record<string, number> = {};
  eventObservables$: Record<string, Observable<any>> = {};
  ioSocket: any;
  emptyConfig: SocketIoConfig = {
    rootUrl: '',
    options: {},
  };

  constructor(private readonly _config: SocketIoConfig) {
    if (this._config === undefined) {
      this._config = this.emptyConfig;
    }
    const rootUrl: string = this._config.rootUrl;
    const options: any = this._config.options;
    const ioFunc = (io as any).default ? (io as any).default : io;
    this.ioSocket = ioFunc(rootUrl, options);
  }

  of(namespace: string) {
    this.ioSocket.of(namespace);
  }

  on(eventName: string, callback: (...args: any[]) => void) {
    this.ioSocket.on(eventName, callback);
  }

  once(eventName: string, callback: (...args: any[]) => void) {
    this.ioSocket.once(eventName, callback);
  }

  connect() {
    return this.ioSocket.connect();
  }

  disconnect(close?: boolean) {
    return this.ioSocket.disconnect(close);
  }

  emit(eventName: string, ...args: any[]) {
    return this.ioSocket.emit(eventName, ...args);
  }

  removeListener(eventName: string, callback?: (...args: any[]) => void) {
    return this.ioSocket.removeListener(eventName, callback);
  }

  removeAllListeners(eventName?: string) {
    return this.ioSocket.removeAllListeners(eventName);
  }

  fromEvent<T>(eventName: string): Observable<T> {
    if (!this.subscribersCounter[eventName]) {
      this.subscribersCounter[eventName] = 0;
    }
    this.subscribersCounter[eventName]++;

    if (!this.eventObservables$[eventName]) {
      this.eventObservables$[eventName] = new Observable((observer: any) => {
        const listener = (data: T) => {
          observer.next(data);
        };
        this.ioSocket.on(eventName, listener);
        return () => {
          this.subscribersCounter[eventName]--;
          if (this.subscribersCounter[eventName] === 0) {
            this.ioSocket.removeListener(eventName, listener);
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.eventObservables$[eventName];
          }
        };
      }).pipe(share());
    }
    return this.eventObservables$[eventName];
  }

  fromOneTimeEvent<T>(eventName: string): Promise<T> {
    return new Promise<T>((resolve) => this.once(eventName, resolve));
  }
}
